import type { FormRule } from 'antd';
import type { TFunction } from 'i18next';
import { ValidateInternshipDates } from '../helper/apiHelper';

interface IntervalFieldNames {
  start: string;
  end: string;
  hours: string;
  // the internship sessions, as ids or as sessions with an id
  sessions?: string;
}

const DEFAULT_FIELDS: IntervalFieldNames = {
  start: 'startDate',
  end: 'endDate',
  hours: 'numberOfHours',
  sessions: 'studyLineIds',
};

const toSessionIds = (value: unknown): Array<number> =>
  Array.isArray(value)
    ? value
        .map((session: unknown) =>
          typeof session === 'object' && session !== null && 'id' in session
            ? Number(session.id)
            : Number(session)
        )
        .filter((id) => Number.isInteger(id))
    : [];

// "2026-10-05" or "2026-10-05T00:00:00" -> "2026-10-05"; null if it isn't a date
const toDateString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const date = value.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

const formatHours = (hours: number): string =>
  String(Math.round(hours * 10) / 10);

/**
 * One check for the whole internship interval, meant for the end date field
 * (with `dependencies` on the start date and the hours):
 * the start can't be after the end, the interval has to be inside one study year,
 * and it has to allow at least the entered number of hours for the students of every selected session.
 */
export const internshipIntervalRule =
  (t: TFunction, fields: IntervalFieldNames = DEFAULT_FIELDS): FormRule =>
  ({ getFieldValue }) => ({
    validator: async (): Promise<void> => {
      const startDate = toDateString(getFieldValue(fields.start));
      const endDate = toDateString(getFieldValue(fields.end));
      if (!startDate || !endDate) return;

      if (startDate > endDate) {
        throw new Error(t('internshipInterval.startAfterEnd'));
      }

      const rawHours = getFieldValue(fields.hours);
      const requiredHours = Number(rawHours);
      const hasHours =
        rawHours !== undefined &&
        rawHours !== null &&
        String(rawHours).trim() !== '' &&
        Number.isFinite(requiredHours) &&
        requiredHours > 0;

      let result;
      try {
        result = await ValidateInternshipDates(
          startDate,
          endDate,
          hasHours ? requiredHours : undefined,
          fields.sessions ? toSessionIds(getFieldValue(fields.sessions)) : []
        );
      } catch {
        throw new Error(t('internshipInterval.validationFailed'));
      }

      if (!result.isInsideStudyYear) {
        throw new Error(t('internshipInterval.outsideStudyYear'));
      }

      if (hasHours && result.isSufficient === false) {
        // the study lines whose students can't work enough hours in the interval are named, one per line;
        // the form item shows the line breaks with white-space: pre-line
        const insufficient = (result.sessions ?? []).filter(
          (session) => session.isSufficient === false
        );
        throw new Error(
          insufficient.length > 0
            ? t('internshipInterval.notEnoughHoursForStudyLines', {
                required: formatHours(requiredHours),
                studyLines: insufficient
                  .map(
                    (session) =>
                      `• ${t('internshipInterval.studyLineHours', {
                        studyLine: session.studyLineName,
                        year: session.yearOfStudy,
                        max: formatHours(session.numberOfHours),
                      })}`
                  )
                  .join('\n'),
              })
            : t('internshipInterval.notEnoughHours', {
                max: formatHours(result.numberOfHours),
                required: formatHours(requiredHours),
              })
        );
      }
    },
  });
